import { useQuery } from '@apollo/client';
import { faArrowLeft, faLock } from '@fortawesome/pro-regular-svg-icons';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import Button from '../../core/components/Button.tsx';
import EmptyStage from '../../core/components/EmptyStage.tsx';
import { withIcon } from '../../core/components/Icon.tsx';
import Layout from '../../core/components/Layout.tsx';
import { GET_NAVIGATION_CHANNELS } from '../../core/components/Navigation/internal/queries.ts';
import Page from '../../core/components/Page';
import SearchField from '../../core/components/SearchField.tsx';
import Tooltip from '../../core/components/Tooltip.tsx';
import { SAD } from '../../core/components/worki/Pose.tsx';
import { useApplicationContext } from '../../core/contexts/ApplicationContext.tsx';
import useSearchParamState from '../../core/utils/useSearchParamState.tsx';
import { TabFilter } from '../../document/pages/Overview/internal/FilterBar.tsx';
import { canSeeDocuments } from '../../users/utils.ts';
import PromptRow from '../components/PromptRow.tsx';
import { CHANNEL_FIELDS, CHAT_PATH, PROMPT_LIBRARY_PATH } from '../constants.ts';
import {
    FEATURED_PROMPTS,
    PROMPT_LIBRARY_CATEGORIES,
    PROMPT_CATEGORY_META,
    PROMPT_ICONS,
    PROMPTS_REQUIRING_ATTACHMENT,
    PromptCategoryKey,
    resolveSingleDocumentType,
    applyDocumentTypePersonalization,
} from '../promptLibraryConfig.ts';
import { renderPromptLibraryText, promptRequiresInput } from '../utils/promptLibrary.ts';

const BackIcon = withIcon(faArrowLeft);
const LockIcon = withIcon(faLock);

const breadcrumbLinkClassName =
    'flex flex-shrink min-w-0 gap-3 rounded transition-colors hover:bg-surface-strong py-1 px-2 -mx-2 items-center cursor-pointer select-text font-semibold text-base text-content-medium hover:text-content-strong';

type CategoryData = Record<PromptCategoryKey, { label: string; prompts: { id: string; text: string }[] }>;

const PromptLibraryPage = () => {
    const { t } = useTranslation('chat');
    const navigate = useNavigate();
    const [search, setSearch] = useSearchParamState('search', '');
    const [activeCategory] = useSearchParamState<'all' | PromptCategoryKey>('category', 'all');

    const categories = t('promptLibrary.categories', { returnObjects: true }) as CategoryData;

    const { user } = useApplicationContext();
    const { data: channelsData } = useQuery(GET_NAVIGATION_CHANNELS, {
        fetchPolicy: 'cache-first',
        skip: !canSeeDocuments(user), // the backend denies the channel lists without the documents permission
    });
    const documentType = resolveSingleDocumentType(channelsData, CHANNEL_FIELDS);

    const featuredPromptKeys = useMemo(
        () => new Set(FEATURED_PROMPTS.map((featured) => `${featured.category}::${featured.promptId}`)),
        []
    );

    const visibleCategories = useMemo(() => {
        return PROMPT_LIBRARY_CATEGORIES.filter((key) => activeCategory === 'all' || activeCategory === key).map(
            (key) => {
                const meta = PROMPT_CATEGORY_META[key];
                const data = categories[key];
                const nonFeatured = data.prompts
                    .filter((prompt) => !featuredPromptKeys.has(`${key}::${prompt.id}`))
                    .map((prompt) => ({
                        ...prompt,
                        text: applyDocumentTypePersonalization(prompt.id, prompt.text, documentType),
                    }));
                const prompts = meta.locked
                    ? nonFeatured
                    : nonFeatured.filter((prompt) => prompt.text.toLowerCase().includes(search.toLowerCase()));
                return { key, meta, label: data.label, prompts };
            }
        );
    }, [activeCategory, categories, search, featuredPromptKeys, documentType]);

    const hasUnlockedCategories = visibleCategories.some(({ meta }) => !meta.locked);
    const hasAnyResults =
        search && hasUnlockedCategories
            ? visibleCategories.some(({ meta, prompts }) => !meta.locked && prompts.length > 0)
            : visibleCategories.some(({ meta, prompts }) => meta.locked || prompts.length > 0);

    const handlePromptClick = (
        prompt: string,
        promptId: string,
        category: PromptCategoryKey,
        requiresAttachment?: boolean
    ) => {
        const requiresInput = promptRequiresInput(prompt, { requiresAttachment });
        navigate(CHAT_PATH, {
            state: {
                ...(requiresInput ? { fillPrompt: prompt } : { sendPrompt: prompt }),
                promptId,
                promptCategory: category,
            },
        });
    };

    const tabHref = (key: 'all' | PromptCategoryKey) =>
        `${PROMPT_LIBRARY_PATH}?category=${key}${search ? `&search=${encodeURIComponent(search)}` : ''}`;

    return (
        <Layout>
            <Page className="flex-1">
                <Page.Header className="justify-between">
                    <div className="flex items-center">
                        <Link
                            to={CHAT_PATH}
                            aria-label={t('new.title')}
                            className={`${breadcrumbLinkClassName} text-content-strong mr-1`}
                        >
                            <Tooltip content={t('new.title')}>
                                <BackIcon />
                            </Tooltip>
                        </Link>
                        <Link to={CHAT_PATH} className={breadcrumbLinkClassName}>
                            {t('new.title')}
                        </Link>
                        <Page.HeaderSeparator />
                        <Page.HeaderTitle>{t('promptLibrary.title')}</Page.HeaderTitle>
                    </div>
                    <SearchField
                        placeholder={t('promptLibrary.searchPlaceholder')}
                        value={search}
                        onValueChange={setSearch}
                    />
                </Page.Header>

                <div className="flex items-center gap-1 border-b border-solid border-line-weak px-6 flex-none overflow-x-auto">
                    <TabFilter to={tabHref('all')} active={activeCategory === 'all'} className="h-14">
                        {t('promptLibrary.allTab')}
                    </TabFilter>
                    {PROMPT_LIBRARY_CATEGORIES.map((key) => {
                        const tab = (
                            <TabFilter
                                key={key}
                                to={tabHref(key)}
                                active={activeCategory === key}
                                className="h-14 whitespace-nowrap text-content-default"
                            >
                                {categories[key].label}
                                {PROMPT_CATEGORY_META[key].locked && (
                                    <Tooltip
                                        content={t(
                                            key === 'email'
                                                ? 'promptLibrary.requiresMailbox'
                                                : 'promptLibrary.requiresErp'
                                        )}
                                    >
                                        <LockIcon className="text-xs text-content-medium" />
                                    </Tooltip>
                                )}
                            </TabFilter>
                        );

                        return tab;
                    })}
                </div>

                <Page.Content lowered className="flex flex-col gap-5">
                    {!hasAnyResults ? (
                        <EmptyStage
                            pose={SAD}
                            title={t('promptLibrary.noResultsTitle')}
                            description={t('promptLibrary.noResultsDescription')}
                            action={
                                search && (
                                    <Button variant="outline" onClick={() => setSearch('')}>
                                        {t('promptLibrary.clearSearch')}
                                    </Button>
                                )
                            }
                        />
                    ) : (
                        visibleCategories.map(({ key, meta, label, prompts }) => {
                            if (!meta.locked && prompts.length === 0) return null;
                            const CategoryIcon = meta.icon;
                            return (
                                <div key={key} className="flex flex-col gap-2.5 pt-2">
                                    <div className="flex items-center gap-2 text-xs">
                                        {activeCategory === 'all' && (
                                            <>
                                                <span className="font-medium text-content-medium">{label}</span>
                                                <span className="text-content-weak">{prompts.length}</span>
                                            </>
                                        )}

                                        {meta.locked && activeCategory === 'all' && (
                                            <div className="ml-auto flex items-center gap-2">
                                                <span className="rounded border border-solid border-line-weak bg-surface-medium px-2 py-1 text-xs font-medium text-content-strong gap-2 cursor-not-allowed">
                                                    <LockIcon className="pr-2" />
                                                    {t(
                                                        key === 'email'
                                                            ? 'promptLibrary.requiresMailbox'
                                                            : 'promptLibrary.requiresErp'
                                                    )}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3">
                                        {prompts.map((prompt) => {
                                            const PromptIcon = PROMPT_ICONS[prompt.id] ?? CategoryIcon;
                                            return (
                                                <PromptRow
                                                    key={prompt.id}
                                                    icon={PromptIcon}
                                                    text={renderPromptLibraryText(prompt.text)}
                                                    disabled={meta.locked}
                                                    onClick={() =>
                                                        handlePromptClick(
                                                            prompt.text,
                                                            prompt.id,
                                                            key,
                                                            PROMPTS_REQUIRING_ATTACHMENT.has(prompt.id)
                                                        )
                                                    }
                                                />
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </Page.Content>
            </Page>
        </Layout>
    );
};

export default PromptLibraryPage;
